import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, OnDestroy, SimpleChanges, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TextareaModule } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { DockModule } from 'primeng/dock';
import { MenuItem } from 'primeng/api';
import { RightDockExpandTriggerComponent } from '../right-dock-expand-trigger/right-dock-expand-trigger.component';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../../../../environments/environment';
import { TranslationService } from '../../../services/translation.service';
import type { PasteTextareaTranslations } from '../../../i18n/paste-textarea';
import en from '../../../i18n/en/paste-textarea';

export type PasteTextareaState = 'normal' | 'warning' | 'error';

@Component({
  selector: 'app-paste-textarea',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TextareaModule,
    ButtonModule,
    MessageModule,
    DockModule,
    RightDockExpandTriggerComponent,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './paste-textarea.component.html',
  styleUrl: './paste-textarea.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasteTextareaComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string = 'Paste Content';
  @Input() icon: string = 'pi pi-file-edit';
  @Input() placeholder: string = 'Paste your content here...';
  @Input() loading: boolean = false;
  @Input() submitText: string = 'Submit';
  @Input() cancelText: string = 'Clear';
  @Input() showCharacterCount: boolean = true;
  @Input() rows: number = 16;
  @Input() errorMessage: string | null = null;
  @Input() translations?: any;
  @Input() value: string = '';
  @Input() isInsideWizard = false;
  @Input() maxChars: number = environment.maxPasteJobDescriptionChars || 8000;
  @Input() warningThreshold: number = 1000;
  
  textValue: string = '';
  dockItems: MenuItem[] = [];

  // Mobile dock state
  isMobileDockOpen = false;
  isMobileView = false;

  // Translations
  private internalTranslations: PasteTextareaTranslations = en;

  @Output() onSubmit = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() valueChange = new EventEmitter<string>();
  @Output() closeWizardRequest = new EventEmitter<void>();

  constructor(private translationService: TranslationService) {}

  ngOnInit(): void {
    this.checkMobileView();
    this.buildDockItems();
    this.loadTranslations();
  }

  private loadTranslations(): void {
    const current = this.translationService.getPasteTextareaTranslations();
    if (current) {
      this.internalTranslations = current;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Sync with value input when it changes
    if (changes['value'] && changes['value'].currentValue !== this.textValue) {
      this.textValue = changes['value'].currentValue || '';
    }
    // Rebuild dock if isInsideWizard changes
    if (changes['isInsideWizard']) {
      this.buildDockItems();
    }
  }

  onTextChange(value: string) {
    this.textValue = value;
    // Emit value change to parent
    this.valueChange.emit(value);
  }

  get characterCount(): number {
    return this.textValue?.length || 0;
  }

  get hasError(): boolean {
    return !!(this.errorMessage);
  }

  get isEmpty(): boolean {
    return !this.textValue?.trim();
  }

  get state(): PasteTextareaState {
    if (this.isOverLimit) return 'error';
    if (this.isNearLimit) return 'warning';
    return 'normal';
  }

  get isOverLimit(): boolean {
    return this.characterCount > this.maxChars;
  }

  get isNearLimit(): boolean {
    return this.characterCount > this.maxChars - this.warningThreshold && !this.isOverLimit;
  }

  get remainingCharacters(): number {
    return Math.max(0, this.maxChars - this.characterCount);
  }

  get excessCharacters(): number {
    return Math.max(0, this.characterCount - this.maxChars);
  }

  get canSubmit(): boolean {
    return !this.loading && !this.isEmpty && !this.isOverLimit;
  }

  get t(): any {
    // If external translations are provided (e.g., from job-description-step), use them
    // Otherwise use the internal translations from TranslationService
    if (this.translations) {
      return {
        ...this.internalTranslations,
        ...this.translations
      };
    }
    return this.internalTranslations;
  }

  handleSubmit(): void {
    if (this.canSubmit) {
      this.onSubmit.emit();
    }
  }

  handleCancel(): void {
    if (!this.loading) {
      this.textValue = '';
      this.valueChange.emit('');
      this.onCancel.emit();
    }
  }

  ngOnDestroy() {
    // Cleanup if needed in the future
  }

  private buildDockItems(): void {
    this.dockItems = [];
    if (this.isInsideWizard) {
      // Hide Dock button (mobile only)
      if (this.isMobileView) {
        this.dockItems.push({
          label: this.t.DOCK_LABEL_HIDE_DOCK || 'Hide Dock',
          icon: 'bi bi-layout-sidebar-reverse',
          tooltipOptions: { tooltipLabel: this.t.DOCK_TOOLTIP_HIDE_DOCK || 'Hide Dock', tooltipPosition: 'top' },
          command: () => this.hideDock(),
        });
      }
      // Exit/Close Wizard button
      this.dockItems.push({
        label: this.t.DOCK_LABEL_EXIT || 'Exit',
        icon: 'bi bi-box-arrow-right',
        tooltipOptions: { tooltipLabel: this.t.DOCK_TOOLTIP_EXIT || 'Exit Wizard', tooltipPosition: 'top' },
        command: () => this.closeWizard(),
      });
    }
  }

  closeWizard(): void {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    this.closeWizardRequest.emit();
  }

  hideDock(): void {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    this.isMobileDockOpen = false;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkMobileView();
    this.buildDockItems();
  }

  checkMobileView(): void {
    this.isMobileView = window.innerWidth <= 768;
  }

  toggleMobileDock(): void {
    this.isMobileDockOpen = !this.isMobileDockOpen;
  }

  interpolate(template: string, params: Record<string, string | number>): string {
    return Object.keys(params).reduce(
      (acc, key) => acc.replaceAll(`{${key}}`, String(params[key])),
      template
    );
  }
}
